import { inject, Injectable, Signal } from '@angular/core';
import { NotificationService } from '@progress/kendo-angular-notification';
import { take } from 'rxjs';
import { SupplierInvoiceCoordinator } from '../SupplierInvoiceCoordinator/supplier-invoice.coordinator';
import {
  ClientOrderItem,
  DocumentSections,
  RouteEntitySubscriberService,
  setSectionValues,
  successSaveToast,
  SupplierInvoiceItem,
  SupplierInvoiceItemMapping,
  SupplierInvoiceItemMappingService,
  roundAmount,
} from 'shared';

@Injectable({ providedIn: 'root' })
export class SupplierInvoiceMappingOpsService {
  // dependencies
  #mappingSvc = inject(SupplierInvoiceItemMappingService);
  #tracker = inject(RouteEntitySubscriberService);
  #notifier = inject(NotificationService);
  #coordinator = inject(SupplierInvoiceCoordinator);

  // signals (pass these in from the component via bind())
  private supplierInvoiceItemMappingsSections!: Signal<DocumentSections<SupplierInvoiceItemMapping> | null>;
  private clientOrderItemsSections!: Signal<DocumentSections<ClientOrderItem> | null>;

  bind(signals: {
    supplierInvoiceItemMappingsSections: Signal<DocumentSections<SupplierInvoiceItemMapping> | null>;
    clientOrderItemsSections: Signal<DocumentSections<ClientOrderItem> | null>;
  }) {
    this.supplierInvoiceItemMappingsSections = signals.supplierInvoiceItemMappingsSections;
    this.clientOrderItemsSections = signals.clientOrderItemsSections;
  }

  remainingInvoiceLineCapacity(
    target: SupplierInvoiceItem,
    mappings: SupplierInvoiceItemMapping[],
    excludeId?: string,
  ) {
    const mapped = mappings.filter((m) => m['@id'] !== excludeId).reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
    return Math.max(roundAmount(target.amount - mapped), 0);
  }

  remainingClientItemAmount(
    clientItem: ClientOrderItem | null,
    mappings: SupplierInvoiceItemMapping[],
    excludeId?: string,
  ) {
    if (!clientItem) return 0;
    const mappedHere = mappings
      .filter((m) => m.clientOrderItem === clientItem['@id'] && m['@id'] !== excludeId)
      .reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
    return Math.max(roundAmount(Number(clientItem.amount) - mappedHere), 0);
  }

  /** Drop core (formerly #performDrop) */
  dropOnInvoiceLine(params: {
    targetLine: SupplierInvoiceItem;
    sectionIri: string;
    draggedIsMapping: boolean;
    mappingId?: string;
    clientOrderItemId: string;
    clientItem: ClientOrderItem;
    previousMappingAmount?: number;
    getItemMappingsForTarget: () => SupplierInvoiceItemMapping[]; // pass in your getSupplierInvoiceItemMappingsForItem
    adjustClientItemMappedAmount: (clientOrderItemIri: string, delta: number) => void;
    syncMappingFormsFromSections: () => void;
  }) {
    const {
      targetLine,
      sectionIri,
      draggedIsMapping,
      mappingId,
      clientOrderItemId,
      clientItem,
      previousMappingAmount,
      getItemMappingsForTarget,
      adjustClientItemMappedAmount,
      syncMappingFormsFromSections,
    } = params;

    const supplierInvoiceItemIri = targetLine['@id']!;
    const sectionMappings = this.supplierInvoiceItemMappingsSections()?.[sectionIri] ?? [];

    // capacities
    const capacityLeft = this.remainingInvoiceLineCapacity(targetLine, getItemMappingsForTarget(), mappingId);
    const clientLeft = this.remainingClientItemAmount(
      clientItem,
      sectionMappings,
      draggedIsMapping ? mappingId : undefined,
    );

    const finalAmount = Math.min(capacityLeft, clientLeft);

    // 1) optimistic mappings
    this.#updateMappings(sectionIri, (current) =>
      draggedIsMapping
        ? current.map((m) =>
            m['@id'] === mappingId ? { ...m, amount: finalAmount, supplierInvoiceItem: supplierInvoiceItemIri } : m,
          )
        : [
            ...current,
            {
              amount: finalAmount,
              supplierInvoiceItem: supplierInvoiceItemIri,
              clientOrderItem: clientOrderItemId,
            } as any,
          ],
    );

    // 2) optimistic UI: nudge the client item's mappedAmount by the known delta
    //    (mappedAmount is a cross-invoice backend total — never recompute it from scratch)
    const delta = finalAmount - (draggedIsMapping ? (previousMappingAmount ?? 0) : 0);
    adjustClientItemMappedAmount(clientOrderItemId, delta);

    // 3) backend
    const payloadBase = { supplierInvoiceItem: supplierInvoiceItemIri, amount: finalAmount };
    this.#tracker.subscribeToItems('supplierInvoice', [targetLine as any, clientItem as any]);

    const request$ = draggedIsMapping
      ? this.#mappingSvc.update(mappingId!, payloadBase as SupplierInvoiceItemMapping)
      : this.#mappingSvc.post({ ...payloadBase, clientOrderItem: clientOrderItemId } as SupplierInvoiceItemMapping);

    request$.pipe(take(1)).subscribe({
      next: (response) => {
        // success toast
        const n = this.#notifier.show(successSaveToast);
        if (n.content) n.content.instance.value = 'SUPPLIER_INVOICE_ITEM_MAPPING';

        // 3b) replace optimistic with real
        this.#updateMappings(sectionIri, (current) =>
          draggedIsMapping
            ? current.map((m) => (m['@id'] === mappingId ? response : m))
            : current.map((m) => {
                const isOptimistic =
                  !m['@id'] &&
                  m.supplierInvoiceItem === supplierInvoiceItemIri &&
                  m.clientOrderItem === clientOrderItemId &&
                  Number(m.amount) === finalAmount;
                return isOptimistic ? response : m;
              }),
        );

        syncMappingFormsFromSections();

        // refresh linked + drafts (local) — brings the authoritative backend mappedAmount
        this.#coordinator.refreshSectionLinkedItems(sectionIri);
        this.#coordinator.refreshSectionDraftItems(sectionIri);

        // wake other tabs
        if (response?.['@id']) {
          this.#tracker.broadcastItemTopicChanged('supplierInvoice', response['@id']!);
          this.#tracker.broadcastItemTopicChanged('supplierInvoice', sectionIri);
        }
      },
      error: () => {
        // rollback by reloading
        // rollback: reload just this section + its related data instead of a non-existent reloadWholeInvoice()
        this.#coordinator.refreshSection(sectionIri);
        this.#coordinator.refreshSectionLinkedItems(sectionIri);
        this.#coordinator.refreshSectionDraftItems(sectionIri);
      },
    });
  }

  #updateMappings = (
    sectionIri: string,
    project: (current: SupplierInvoiceItemMapping[]) => SupplierInvoiceItemMapping[],
  ) => {
    const sections = this.supplierInvoiceItemMappingsSections();
    const current = (sections?.[sectionIri] ?? []) as SupplierInvoiceItemMapping[];
    const next = project(current);
    // mutate via setSectionValues -> delegate to coordinator/store if you prefer
    (this.supplierInvoiceItemMappingsSections as any).update((s: any) => setSectionValues(s, sectionIri, next));
  };
}
